import * as React from 'react';
import { useCallback, useState } from 'react';
import { observer } from 'mobx-react';

import { Viewport, ViewSmall, ViewLarge } from 'bernie-view';
import { UitkLayoutGridItem } from 'uitk-react-layout-grid';
import { UitkSelect } from 'uitk-react-select';

import { SearchFormOptionsList, WizardMenu } from '.';
import { trackAnalytics } from 'utility';
import { useStores } from 'utility/hooks';
import { FlightsSearchFormCabinClassOption, FlightsSearchFormCabinClassOptionItem } from 'types/search/wizard';
import { InteractableFieldComponentProps } from 'types/shared';

interface CabinClassOptionsProps extends InteractableFieldComponentProps {
  searchOption: FlightsSearchFormCabinClassOption;
}

export const CabinClassOptions = observer(({ searchOption, onFieldInteraction }: CabinClassOptionsProps) => {
  const { analytics, searchFormStore } = useStores();
  const [menuOpen, setMenuOpen] = useState(false);
  const { fieldIcon, icon, items, label, selectedItem, openAnalytics } = searchOption;
  const selectedCabinClass = selectedItem.cabinClass;
  const loading = searchFormStore.searchForm.__typename === 'FlightsLoadingSearchForm';
  const triggerLabel = selectedItem.label;
  const cabinClassDataTestId = 'flights-cabin-class-options-toggle';

  const updateCabinClassPreference = useCallback(
    (selectedCabinClass: string): void => {
      const { items } = searchOption;
      const previousCabinClass = items.find(item => item.selected);

      items.forEach(item => {
        item.selected = selectedCabinClass === item.cabinClass;
      });

      const selectedCabinClassItem = items.find(item => item.selected);

      searchOption.selectedItem = selectedCabinClassItem;
      if (previousCabinClass.cabinClass !== selectedCabinClassItem.cabinClass) {
        trackAnalytics(selectedCabinClassItem.cabinClassSelectedAnalytics, analytics);
      }
    },
    [analytics, searchOption]
  );

  const selectOnChange = useCallback(
    (input): void => {
      const inputValue: string = input.target.value;
      updateCabinClassPreference(inputValue);
    },
    [updateCabinClassPreference]
  );

  const menuListItemMapper = useCallback(() => {
    const updateFnRef = updateCabinClassPreference;

    return searchOption.items.map(item => ({
      attributes: {
        role: 'menuItem',
        tabindex: 0
      },
      className: 'uitk-list-item',
      onClick() {
        updateFnRef(item.cabinClass);
      },
      label: item.label,
      typeAnchor: false
    }));
  }, [searchOption, updateCabinClassPreference]);

  const toggleMenu = () => {
    const isOpen = !menuOpen;

    onFieldInteraction && onFieldInteraction();
    isOpen && trackAnalytics(openAnalytics, analytics);

    return setMenuOpen(menuOpen => !menuOpen);
  };

  return (
    <Viewport>
      <ViewSmall>
        {!loading && (
          <UitkLayoutGridItem colSpan={{ small: 2, medium: 1 }}>
            <div>
              <UitkSelect
                data-test-id={cabinClassDataTestId}
                disabled={false}
                label={label}
                hasVisibleLabel={true}
                value={selectedCabinClass}
                onChange={selectOnChange}
                onClick={onFieldInteraction}
                icon={fieldIcon && fieldIcon.id}
              >
                {items.map((item: FlightsSearchFormCabinClassOptionItem) => {
                  const { cabinClass, label } = item;
                  const cabinClassId = `cabin-class-option-${cabinClass}`;

                  return (
                    <option data-test-id={cabinClassId} key={cabinClassId} value={cabinClass}>
                      {label}
                    </option>
                  );
                })}
              </UitkSelect>
            </div>
          </UitkLayoutGridItem>
        )}
      </ViewSmall>
      <ViewLarge>
        <WizardMenu
          toggleMenu={toggleMenu}
          isOpen={menuOpen}
          label={triggerLabel}
          labelIcon={icon.id}
          dataTestId={cabinClassDataTestId}
        >
          {!loading && <SearchFormOptionsList items={menuListItemMapper()} />}
        </WizardMenu>
      </ViewLarge>
    </Viewport>
  );
});
